import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  axiosInstance,
  useClientInfo,
  useAppContext,
} from "../utils/commonImports";
import { getFYRange, resolveDateRange } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

const MISCustomerVehicle = () => {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();

  // Financial year selected in the header
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // e.g. "2025-26"
  const { start: fyStart, end: fyEnd } = getFYRange(selectedFinancialYear);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const reportRef = useRef(null);

  const [reportType, setReportType] = useState("customer-vehicle-group");

  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs());

  /* =========================
     BACK BUTTON -> Reports dashboard
  ========================= */
  const handleBack = () => navigate(REPORTS_PATH);

  /* =========================
     LOAD DATA (FY aware)
     `override` lets the FY effect load right away with the new range,
     before the date state has updated.
  ========================= */
  const fetchData = async (override) => {
    if (!clientId || financialYearLoading) return;

    const { from, to } =
      override || resolveDateRange(selectedFinancialYear, fromDate, toDate);

    setLoading(true);
    setReportError("");

    try {
      const res = await axiosInstance.get(`/api/mis/${reportType}`, {
        params: {
          clientId,
          client_id: clientId,
          fromDate: from || null,
          toDate: to || null,
          fyid: selectedFinancialYear?.fyid || "",
          fy_id: selectedFinancialYear?.fyid || "",
          fy,
        },
      });

      const result = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.rows)
            ? res.data.rows
            : [];

      setRows(result);
    } catch (err) {
      console.error("MIS Report Load Error:", err.response?.data || err);
      setRows([]);
      setReportError(
        err.response?.data?.message ||
        err.message ||
        "Failed to load MIS report."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchData();

  const handlePrint = () => window.print();

  // Opens browser print dialog; select "Save as PDF" to create a PDF.
  const handlePdf = () => window.print();

  const handleExcel = () => {
    const headers = ["Customer", ...columns, "Total"];
    const escapeCsv = (value) => {
      const text = value == null ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const csvRows = Object.entries(pivotData).map(([customer, values]) => [
      customer,
      ...columns.map((col) => Number(values[col] || 0)),
      getRowTotal(values),
    ]);

    csvRows.push([
      "Grand Total",
      ...columns.map((col) => Number(columnTotals[col] || 0)),
      grandTotal,
    ]);

    const csv = "\\uFEFF" + [
      headers.map(escapeCsv).join(","),
      ...csvRows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\\r\\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `MIS_${reportType}_${fy || "Report"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleScreenshot = async () => {
    if (!reportRef.current) return;

    try {
      setReportError("");
      const module = await import("html2canvas");
      const html2canvas = module.default || module;
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = `MIS_${reportType}_${fy || "Report"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("MIS screenshot error:", err);
      setReportError(
        'Screenshot requires html2canvas. Run "npm install html2canvas" and try again.'
      );
    }
  };

  // Client or FY changed: reset the dates to the whole FY and load it
  useEffect(() => {
    if (!clientId || financialYearLoading) return;

    const { start, end } = getFYRange(selectedFinancialYear);
    if (start && end) {
      setFromDate(dayjs(start));
      setToDate(dayjs(end));
      fetchData({ from: start, to: end });
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedFinancialYear?.fyid, financialYearLoading, reportType]);

  // 🔹 Dynamic column key based on report type
  const dynamicKey = useMemo(() => {
    if (reportType === "customer-vehicle-group") return "vehicle_group";
    if (reportType === "customer-vehicle") return "vehiclename";
    if (reportType === "customer-vendor") return "vendorname";
    return "";
  }, [reportType]);

  // 🔹 Extract dynamic columns
  const columns = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      set.add(r[dynamicKey] || "OTHERS");
    });
    return Array.from(set).sort();
  }, [rows, dynamicKey]);

  // 🔹 Pivot (adds up rows that share the same customer and column)
  const pivotData = useMemo(() => {
    const grouped = {};

    rows.forEach((row) => {
      const key = row[dynamicKey] || "OTHERS";

      if (!grouped[row.customername]) {
        grouped[row.customername] = {};
      }

      grouped[row.customername][key] =
        (grouped[row.customername][key] || 0) + Number(row.total_amount || 0);
    });

    return grouped;
  }, [rows, dynamicKey]);

  // 🔹 Column totals
  const columnTotals = useMemo(() => {
    const totals = {};

    columns.forEach((col) => {
      totals[col] = rows
        .filter((r) => (r[dynamicKey] || "OTHERS") === col)
        .reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
    });

    return totals;
  }, [rows, columns, dynamicKey]);

  const getRowTotal = (values) => {
    return columns.reduce((sum, col) => sum + Number(values[col] || 0), 0);
  };

  const grandTotal = Object.values(columnTotals).reduce(
    (sum, val) => sum + val,
    0,
  );

  const pickerLimits = {
    minDate: fyStart ? dayjs(fyStart) : undefined,
    maxDate: fyEnd ? dayjs(fyEnd) : undefined,
  };

  return (
    <Box sx={{ p: 2, "@media print": {
      "& .no-print": { display: "none !important" },
      "& .MuiPaper-root": { boxShadow: "none !important" },
      "& .MuiTableContainer-root": { maxHeight: "none !important", overflow: "visible !important" },
      "& table": { minWidth: "0 !important" },
      "& th, & td": { fontSize: "8px !important", padding: "3px !important" },
    } }}>
      {/* 🔹 HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              MIS Report
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            MIS Report
            {fy && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                FY {fy}
              </Typography>
            )}
          </Typography>
        </Box>
        <Box className="no-print" display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ ml: "auto" }}>
          <Button variant="contained" size="small" startIcon={<SearchIcon />}
            onClick={handleSearch} disabled={loading || financialYearLoading || !clientId}>
            Search
          </Button>
          <Button variant="outlined" size="small" startIcon={<PrintIcon />}
            onClick={handlePrint} disabled={loading || rows.length === 0}>
            Print
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<PictureAsPdfIcon />}
            onClick={handlePdf} disabled={loading || rows.length === 0}>
            PDF
          </Button>
          <Button variant="outlined" color="success" size="small" startIcon={<FileDownloadIcon />}
            onClick={handleExcel} disabled={loading || rows.length === 0}>
            Excel
          </Button>
          <Button variant="outlined" color="secondary" size="small" startIcon={<CameraAltIcon />}
            onClick={handleScreenshot} disabled={loading || rows.length === 0}>
            Screenshot
          </Button>
        </Box>
      </Box>

      {/* 🔹 FILTER SECTION */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box className="no-print" sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              label="Report Type"
              onChange={(e) => {
                setReportType(e.target.value);
                setRows([]); // clear old data
              }}
            >
              <MenuItem value="customer-vehicle-group">
                Customer Vs Vehicle Group
              </MenuItem>
              <MenuItem value="customer-vehicle">Customer Vs Vehicle</MenuItem>
              <MenuItem value="customer-vendor">Customer Vs Vendor</MenuItem>
            </Select>
          </FormControl>

          <Grid item xs={12} sm={2.2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              format="DD/MM/YYYY"
              onChange={(newValue) => setFromDate(newValue)}
              {...pickerLimits}
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.2}>
            <DatePicker
              label="To Date"
              value={toDate}
              format="DD/MM/YYYY"
              onChange={(newValue) => setToDate(newValue)}
              {...pickerLimits}
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
            />
          </Grid>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading || financialYearLoading || !clientId}>
            Search
          </Button>
        </Box>
      </LocalizationProvider>

      {reportError && <Alert severity="error" className="no-print" sx={{ mb: 1 }}>{reportError}</Alert>}

      <Box ref={reportRef}>
      {/* 🔹 REPORT TITLE */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          MIS REPORT
        </Typography>

        <Typography sx={{ fontSize: "0.8rem" }}>
          {reportType.replaceAll("-", " ").toUpperCase()}
          {fy ? ` (FY ${fy})` : ""}
        </Typography>

        <Typography sx={{ fontSize: "0.75rem" }}>
          {fromDate?.format("DD/MM/YYYY")} to {toDate?.format("DD/MM/YYYY")}
        </Typography>
      </Box>

      {/* 🔹 TABLE */}
      <TableContainer
        component={Paper}
        sx={{
          overflowX: "auto",
          border: "1px solid #bbb",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: columns.length * 120, // auto grow based on columns
            borderCollapse: "collapse",
            "& th, & td": {
              border: "1px solid #bbb",
              fontSize: "0.70rem",
              padding: "4px 6px",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>
                Customer
              </TableCell>

              {columns.map((col) => (
                <TableCell key={col} align="right" sx={{ fontWeight: 700 }}>
                  {col}
                </TableCell>
              ))}

              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center">
                  No Data Found
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(pivotData).map(([customer, values]) => (
                <TableRow key={customer}>
                  <TableCell>{customer}</TableCell>

                  {columns.map((col) => (
                    <TableCell key={col} align="right">
                      {values[col] || 0}
                    </TableCell>
                  ))}

                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {getRowTotal(values)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>

          <TableFooter>
            <TableRow sx={{ backgroundColor: "#e5e7eb" }}>
              <TableCell sx={{ fontWeight: 700 }}>Grand Total</TableCell>

              {columns.map((col) => (
                <TableCell key={col} align="right" sx={{ fontWeight: 700 }}>
                  {columnTotals[col] || 0}
                </TableCell>
              ))}

              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {grandTotal}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      </Box>
    </Box>
  );
};

export default MISCustomerVehicle;
